"use client";

import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { type ReactNode, useRef } from "react";
import { useRevokeUserInvite } from "./api/user.js";
import { AlertDialog, Button } from "./elements.js";
import { Member } from "../api/endpoint.js";
import { Translation } from "./i18n/translation.js";

interface RevokeInviteDialogProps extends AlertDialog.RootProps {
  user: Member;
  children?: ReactNode;
}

export function RevokeInviteDialog({
  children,
  user,
  ...props
}: RevokeInviteDialogProps) {
  const revokeInvite = useRevokeUserInvite();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const onSubmitForm = () => {
    revokeInvite.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          props.onOpenChange?.(false);
        },
      },
    );
  };

  return (
    <AlertDialog.Root {...props}>
      {children && <AlertDialog.Trigger>{children}</AlertDialog.Trigger>}

      <AlertDialog.Content
        maxWidth="480px"
        onOpenAutoFocus={() => {
          requestAnimationFrame(() => {
            cancelButtonRef.current?.focus();
          });
        }}
      >
        <AlertDialog.Title>
          <Translation
            defaultMessage="Revoke invite?"
            id="/fEPAD"
            description="Dialog title asking to confirm revoking an invitation"
          />
        </AlertDialog.Title>
        <Flex mb="4" direction="column" gap="3">
          <AlertDialog.Description>
            <Translation
              defaultMessage="Are you sure you want to revoke the invite to {email}? This action is immediate and cannot be undone."
              id="wt2qe9"
              description="Warning message about revoking invite to specific email"
              values={{ email: <Text weight="bold">{user.email}</Text> }}
            />
          </AlertDialog.Description>
        </Flex>

        {revokeInvite.error ? (
          <Callout.Root color="red" mt="4" mb="-2">
            <Callout.Text>
              {getMutationErrorMessage(revokeInvite.error)}
            </Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex gap="3" justify="end" mt="5" asChild>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmitForm();
            }}
          >
            <AlertDialog.Cancel>
              <Button
                variant="secondary"
                ref={cancelButtonRef}
                disabled={revokeInvite.isPending}
              >
                <Translation
                  defaultMessage="Cancel"
                  id="+p4qwy"
                  description="Button to cancel revoking invite"
                />
              </Button>
            </AlertDialog.Cancel>

            <Button
              variant="destructive"
              type="submit"
              loading={revokeInvite.isPending}
            >
              <Translation
                defaultMessage="Revoke"
                id="Nkz6Xi"
                description="Button to confirm revoking the invitation"
              />
            </Button>
          </form>
        </Flex>
      </AlertDialog.Content>

      {/* mirror errors in a live region */}
      <VisuallyHidden asChild>
        <section aria-live="polite">
          {getMutationErrorMessage(revokeInvite.error)}
        </section>
      </VisuallyHidden>
    </AlertDialog.Root>
  );
}

function getMutationErrorMessage(error: unknown) {
  if (!error) {
    return null;
  }
  // TODO Handle server errors
  return (
    <Translation
      defaultMessage="There was an error revoking the invite. Please try again."
      id="zG7XDc"
      description="Error message shown when revoking invite fails"
    />
  );
}
